/*
  EasyUSA add-to-cart morph.

  One shape, never swapped: the button's skin morphs Idle pill → Loading
  circle → Confirmed check → "Added · N" pill → Idle, while its content
  swaps with a short blur. The button's own box never changes size, so the
  surrounding layout never shifts — only the absolutely positioned skin moves.

  Usage (surfaces own their fetch; this only owns the motion):
    var morph = EuAtcMorph.enhance(button);
    morph.start();                          // on submit
    morph.succeed('Added · 3').then(fn);    // fn runs once the check has landed
    morph.fail();                           // on error (optional label, e.g. 'Try Again')
    morph.setLabel('Sold Out');             // idle label changes (variant switch)
    morph.onIdle = fn;                      // optional: decide enabled state after a
                                            // flow ends (default: re-enable)

  Buttons with [data-eu-morph] are enhanced automatically on load. For a button
  with an icon beside its text, mark the text element [data-eu-morph-text];
  icon and text then swap together and only the text changes.
*/
(function () {
  'use strict';
  if (window.EuAtcMorph) return;

  var reduceQuery = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  function reduced() { return !!(reduceQuery && reduceQuery.matches); }

  // Timings (ms). MIN_LOADING keeps the spinner from flickering on fast responses.
  var MIN_LOADING = 200;  // ≈ when the circle has formed, so success flows straight on
  var CHECK_DRAW = 480;  // check draws (~320ms) and holds a beat before the reopen
  var HANDOFF = 800;     // check landed + button reopening → caller opens the drawer
  var HOLD_ADDED = 1800; // "Added · N" stays this long before returning to idle
  var LABEL_OUT = 120;

  // ── Springs ────────────────────────────────────────────────────
  // Closed-form step response of a damped spring (0 → 1). zeta just under 1
  // gives at most a tiny overshoot, never a bounce.
  function stepResponse(t, omega, zeta) {
    if (t <= 0) return 0;
    if (zeta >= 1) return 1 - Math.exp(-omega * t) * (1 + omega * t);
    var wd = omega * Math.sqrt(1 - zeta * zeta);
    return 1 - Math.exp(-zeta * omega * t) *
      (Math.cos(wd * t) + (zeta * omega / wd) * Math.sin(wd * t));
  }

  // A value whose target changes many times is the sum of one spring per
  // change, so it stays a pure function of time and retargets smoothly.
  function Spring(value, omega, zeta) {
    this.base = value;
    this.target = value;
    this.changes = [];
    this.omega = omega;
    this.zeta = zeta;
    // Time for the envelope to decay below 0.1% of the change.
    this.settleMs = 1000 * Math.log(1000) / (Math.min(zeta, 1) * omega);
  }
  Spring.prototype.set = function (target, now) {
    if (target === this.target) return;
    if (reduced()) { this.jump(target); return; }
    this.changes.push({ t: now, d: target - this.target });
    this.target = target;
  };
  Spring.prototype.jump = function (value) {
    this.base = value;
    this.target = value;
    this.changes = [];
  };
  Spring.prototype.at = function (now) {
    var live = [];
    var moving = 0;
    for (var i = 0; i < this.changes.length; i++) {
      var c = this.changes[i];
      var age = now - c.t;
      // Settled changes fold into base so the list never grows unbounded.
      if (age >= this.settleMs) { this.base += c.d; continue; }
      moving += c.d * stepResponse(age / 1000, this.omega, this.zeta);
      live.push(c);
    }
    this.changes = live;
    return this.base + moving;
  };
  Spring.prototype.busy = function () { return this.changes.length > 0; };

  // ── Markup ─────────────────────────────────────────────────────
  var ICON =
    '<svg class="eu-morph__icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">' +
      '<circle class="eu-morph__spinner" cx="12" cy="12" r="9" pathLength="100"></circle>' +
      '<path class="eu-morph__check" d="M6.5 12.5l3.6 3.6 7.4-8.2" pathLength="1"></path>' +
    '</svg>';

  function Morph(btn) {
    this.btn = btn;

    var shape = document.createElement('span');
    shape.className = 'eu-morph__shape';
    shape.setAttribute('aria-hidden', 'true');

    // label: everything visible in the button (blur-swapped as one piece).
    // text: the part whose words change.
    var label = document.createElement('span');
    label.className = 'eu-morph__label';
    var text = btn.querySelector('[data-eu-morph-text]');
    if (text) {
      while (btn.firstChild) label.appendChild(btn.firstChild);
    } else {
      text = document.createElement('span');
      text.textContent = btn.textContent.trim();
      btn.textContent = '';
      label.appendChild(text);
    }
    this.idleLabel = text.textContent.trim();

    btn.appendChild(shape);
    btn.appendChild(label);
    btn.insertAdjacentHTML('beforeend', ICON);
    btn.classList.add('eu-morph');

    var sr = document.createElement('span');
    sr.className = 'eu-morph__sr';
    sr.setAttribute('role', 'status');
    sr.setAttribute('aria-live', 'polite');
    btn.insertAdjacentElement('afterend', sr);

    this.shape = shape;
    this.label = label;
    this.text = text;
    this.icon = btn.querySelector('.eu-morph__icon');
    this.sr = sr;
    this.onIdle = null;

    // open: 1 = full pill, 0 = circle. done: success colour overlay.
    // press: scale dip on click.
    this.open = new Spring(1, 26, 0.86);
    this.done = new Spring(0, 30, 1);
    this.press = new Spring(1, 40, 0.8);
    this.shakeAt = -1;

    this.radiusIdle = parseFloat(getComputedStyle(btn).getPropertyValue('--eu-morph-radius')) || 12;
    this.timers = [];
    this.frame = 0;
    this.lastH = -1;
    this.setState('idle');
  }

  Morph.prototype.setState = function (state) {
    this.state = state;
    this.btn.setAttribute('data-eu-morph-state', state);
  };

  Morph.prototype.later = function (ms, fn) {
    var id = setTimeout(fn, ms);
    this.timers.push(id);
    return id;
  };
  Morph.prototype.clearTimers = function () {
    this.timers.forEach(clearTimeout);
    this.timers = [];
  };

  Morph.prototype.kick = function () {
    if (this.frame) return;
    var self = this;
    function tick(now) {
      self.frame = 0;
      if (self.render(now)) self.frame = requestAnimationFrame(tick);
    }
    this.frame = requestAnimationFrame(tick);
  };

  // Writes the skin for time `now`. Returns true while anything is moving.
  Morph.prototype.render = function (now) {
    var w = this.btn.clientWidth;
    var h = this.btn.clientHeight;
    var open = this.open.at(now);
    var shaking = this.shakeAt >= 0;
    var moving = this.open.busy() || this.done.busy() || this.press.busy() || shaking;

    // At rest the skin is plain CSS (100% width), so buttons that were hidden
    // when measured — e.g. inside an inactive tab — still render correctly.
    if (!moving && this.state === 'idle') {
      this.shape.style.width = '';
      this.shape.style.borderRadius = '';
      this.shape.style.transform = '';
      this.shape.style.removeProperty('--eu-morph-done-opacity');
      return false;
    }

    // Spinner/check scale with the button (24px on the PDP, smaller on cards).
    if (h !== this.lastH && h > 0) {
      this.lastH = h;
      this.icon.style.setProperty('--eu-morph-icon', Math.min(24, Math.round(h * 0.5)) + 'px');
    }
    var done = this.done.at(now);
    var press = this.press.at(now);

    var radiusIdle = this.radiusIdle;
    var width = h + (w - h) * open;
    var radius = radiusIdle + (h / 2 - radiusIdle) * (1 - Math.max(0, Math.min(1, open)));

    // Error shake: a short decaying sideways nudge, two visible swings.
    var shakeX = 0;
    shaking = false;
    if (this.shakeAt >= 0) {
      var s = (now - this.shakeAt) / 1000;
      if (s < 0.45) {
        shakeX = 5 * Math.exp(-s * 9) * Math.sin(s * Math.PI * 2 * 5.5);
        shaking = true;
      } else {
        this.shakeAt = -1;
      }
    }

    this.shape.style.width = width.toFixed(2) + 'px';
    this.shape.style.borderRadius = radius.toFixed(2) + 'px';
    this.shape.style.transform =
      'translateX(calc(-50% + ' + shakeX.toFixed(2) + 'px)) scale(' + press.toFixed(4) + ')';
    this.shape.style.setProperty('--eu-morph-done-opacity', Math.max(0, Math.min(1, done)).toFixed(3));

    var still = this.open.busy() || this.done.busy() || this.press.busy() || shaking;
    if (!still && this.state === 'idle') return this.render(now);
    return still;
  };

  // Blur-swap the visible label. Exit first, then enter, so text never overlaps.
  Morph.prototype.swapLabel = function (words, show) {
    var label = this.label;
    var text = this.text;
    label.classList.add('is-out');
    this.later(LABEL_OUT, function () {
      text.textContent = words;
      if (show) label.classList.remove('is-out');
    });
  };

  Morph.prototype.setLabel = function (text) {
    this.idleLabel = text;
    if (this.state !== 'idle') return;
    if (this.text.textContent === text) return;
    if (reduced() || !this.btn.isConnected) { this.text.textContent = text; return; }
    this.swapLabel(text, true);
  };

  Morph.prototype.start = function () {
    var now = performance.now();
    this.clearTimers();
    this.startedAt = now;
    this.btn.disabled = true;
    this.btn.setAttribute('aria-busy', 'true');
    this.setState('loading');
    this.sr.textContent = 'Adding to cart';

    this.press.set(0.97, now);
    this.later(80, function () { this.press.set(1, performance.now()); this.kick(); }.bind(this));
    this.open.set(0, now);
    this.done.set(0, now);
    this.label.classList.add('is-out');
    this.later(LABEL_OUT, function () { this.text.textContent = 'Adding…'; }.bind(this));
    this.kick();
  };

  Morph.prototype.succeed = function (addedText) {
    var self = this;
    var wait = Math.max(0, MIN_LOADING - (performance.now() - (this.startedAt || 0)));
    return new Promise(function (resolve) {
      self.later(wait, function () {
        var now = performance.now();
        self.setState('success');
        self.done.set(1, now);
        self.kick();

        // Check lands, then the circle reopens into "Added · N".
        self.later(CHECK_DRAW, function () {
          self.setState('added');
          self.open.set(1, performance.now());
          self.text.textContent = addedText;
          self.label.classList.remove('is-out');
          self.sr.textContent = addedText.replace(/\s*·\s*/, ' ') + ' to cart';
          self.kick();
        });

        self.later(HANDOFF, resolve);
        self.later(CHECK_DRAW + HOLD_ADDED, function () { self.reset(); });
      });
    });
  };

  Morph.prototype.fail = function (errorLabel) {
    var now = performance.now();
    this.clearTimers();
    this.setState('error');
    this.open.set(1, now);
    this.done.set(0, now);
    if (!reduced()) this.shakeAt = now;
    this.text.textContent = errorLabel || this.idleLabel;
    this.label.classList.remove('is-out');
    this.sr.textContent = errorLabel || '';
    this.release();
    this.kick();
    this.later(500, function () {
      if (this.state === 'error') { this.setState('idle'); this.kick(); }
    }.bind(this));
  };

  Morph.prototype.reset = function () {
    var now = performance.now();
    this.setState('idle');
    this.done.set(0, now);
    this.open.set(1, now);
    this.swapLabel(this.idleLabel, true);
    this.release();
    this.kick();
  };

  Morph.prototype.release = function () {
    this.btn.removeAttribute('aria-busy');
    if (typeof this.onIdle === 'function') this.onIdle();
    else this.btn.disabled = false;
  };

  function enhance(btn) {
    if (!btn) return null;
    if (!btn.euMorph) btn.euMorph = new Morph(btn);
    return btn.euMorph;
  }

  window.EuAtcMorph = { enhance: enhance };

  function enhanceAll() {
    document.querySelectorAll('[data-eu-morph]').forEach(enhance);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', enhanceAll);
  } else {
    enhanceAll();
  }

  // Resizes change the pill's full width; redraw once so the skin tracks it.
  window.addEventListener('resize', function () {
    document.querySelectorAll('.eu-morph').forEach(function (btn) {
      if (btn.euMorph) btn.euMorph.render(performance.now());
    });
  });
}());
